import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckIcon, DownloadIcon, EyeIcon, GiftIcon, LayersIcon, SparklesIcon } from 'lucide-react';
import { CheckoutButton } from './Button';
import { ProductName } from './ProductName';
import { Ornament } from './Ornament';
import { PriceTag } from './PriceTag';
const includes = [{
  label: 'Volume 1 — Beginner Barre: 25 Ready-to-Teach Classes'
}, {
  label: 'Volume 2 — Glutes, Thighs & Lower Body: 25 Ready-to-Teach Classes'
}, {
  label: 'Volume 3 — Core, Balance & Stability: 25 Ready-to-Teach Classes'
}, {
  label: 'Volume 4 — Arms, Upper Body & Posture: 25 Ready-to-Teach Classes'
}, {
  label: 'Volume 5 — Cardio & Full-Body Barre: 25 Ready-to-Teach Classes'
}, {
  label: 'Volume 6 — Progressive Barre Class Series: 25 Ready-to-Teach Classes'
}, {
  label: '100% Visual Core Teaching System'
}, {
  label: '150 Ready-to-Teach Classes'
}, {
  label: 'Beginner-to-Advanced Progressions'
}, {
  label: 'Instructor Coaching Cues'
}, {
  label: 'Modifications & Level-Up Options'
}, {
  label: 'Visual Corrections'
}, {
  label: 'Ready-to-Teach Class Timelines'
}, {
  label: 'Barre Instructor Cueing Playbook',
  bonus: true
}, {
  label: 'Barre Modifications & Progressions Handbook',
  bonus: true
}, {
  label: 'Barre Class Planning & Sequencing System',
  bonus: true
}, {
  label: 'Barre Alignment & Common Mistakes Handbook',
  bonus: true
}, {
  label: '50 Barre Class Themes & Focus Ideas',
  bonus: true
}, {
  label: 'Immediate Digital Access'
}, {
  label: 'PDF Format'
}];
const alternatives = ['Beginner programming.', 'Lower-body programming.', 'Core and balance.', 'Upper-body Barre.', 'Cardio and full-body Barre.', 'Progressive programming.', '150 complete classes.', 'Visual teaching tools.', 'Modifications.', 'Corrections.', 'Instructor cues.'];
const trustItems = [{
  icon: LayersIcon,
  label: '6 Complete Instructor Volumes'
}, {
  icon: BookOpenIcon,
  label: '150 Ready-to-Teach Classes'
}, {
  icon: EyeIcon,
  label: '100% Visual Core Material'
}, {
  icon: GiftIcon,
  label: '5 Professional Bonus Guides'
}, {
  icon: DownloadIcon,
  label: 'Immediate Access'
}];
const alreadyHave = ['You already have the class structure.', 'You already have the sequence.', 'You already have the visual teaching tools.', 'You already have the modifications.', 'You already have the progressions.'];
export function PricingSection() {
  return <section id="pricing" className="bg-inverse px-4 py-20 text-inverse-foreground sm:py-28" data-id="element-250" data-mp="3uuthek3uawrq">
      <div className="mx-auto max-w-3xl" data-id="element-251" data-mp="3jtie36vjlcx">
        <div className="text-center" data-id="element-252" data-mp="3kz9qkc44uo9u">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-inverse-foreground/70" data-id="element-253" data-mp="3l0bbr9ev4r5a">
            Your Complete Barre Instructor Ready Package
          </p>
          <h2 className="mt-3 text-3xl font-semibold text-inverse-foreground sm:text-4xl" data-id="element-254" data-mp="3bz3fsxztk2ed">
            A Complete 6-in-1 Visual Teaching System —{' '}
            <span className="italic text-accent-soft" data-id="element-255" data-mp="2xb3f943iavkk">Ready for Your Next Class</span>
          </h2>
          <Ornament className="mt-5" data-id="element-256" data-mp="2sc09tijm6nc2" />
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative mt-12 rounded-2xl border border-accent/50 bg-inverse-raised shadow-elevated" data-id="element-257" data-mp="2sdbju03nkrbm">
          <div className="absolute -top-3.5 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-accent bg-primary px-3.5 py-1 text-xs font-semibold uppercase tracking-[0.1em] text-primary-foreground" data-id="element-258" data-mp="3g7uwl7zhn0lz">
            <SparklesIcon size={14} className="text-primary-foreground" aria-hidden="true" data-id="element-259" data-mp="3l1qnjqhc3n7n" />{' '}
            6-in-1 System
          </div>
          <div className="p-6 text-center sm:p-10" data-id="element-260" data-mp="2syituvf4n7i">
            <h3 className="text-3xl font-semibold text-inverse-foreground" data-id="element-261" data-mp="3u25d521bzogy">
              <ProductName accentClassName="text-accent-soft" data-id="element-262" data-mp="hycbsmf1w85y" />
            </h3>
            <p className="mt-3 text-xs font-bold tracking-[0.16em] text-accent" data-id="element-263" data-mp="hw9nq7xgkp3v">
              6 VOLUMES. 150 CLASSES. 5 BONUSES. ONE COMPLETE SYSTEM.
            </p>
            <p className="mt-7 text-left text-sm font-semibold text-inverse-foreground sm:mx-auto sm:max-w-xl" data-id="element-264" data-mp="qqxszy31a6so">
              You Receive:
            </p>
            <ul className="mx-auto mt-3 grid max-w-xl gap-y-2.5 border-y border-accent/25 py-6 text-left" data-id="element-265" data-mp="3v54cwd1el3w2">
              {includes.map((item, index) => {
              const Icon = item.bonus ? GiftIcon : CheckIcon;
              const startsBonuses = item.bonus && !includes[index - 1]?.bonus;
              return <React.Fragment key={item.label} data-id="element-266" data-mp="d0km7z1voy4x" data-mpi={index}>
                    {startsBonuses && <li className="pt-3 text-sm font-semibold text-accent-soft" data-id="element-267" data-mp="1jejw758cn9f0">
                        PLUS:
                      </li>}
                    <li className="flex gap-2.5 text-sm leading-6 text-inverse-foreground/85" data-id="element-268" data-mp="38wqikuupwmv7">
                      <Icon size={16} strokeWidth={1.75} className="mt-1 shrink-0 text-accent" aria-hidden="true" data-id="element-269" data-mp="33wko76x2cm9f" />
                      <span data-id="element-270" data-mp="2u6jc7dhziuav">
                        {item.bonus && <span className="mr-1.5 rounded-sm bg-primary px-1.5 py-0.5 text-[10px] font-bold tracking-[0.08em] text-primary-foreground" data-id="element-271" data-mp="2q5zf29xevqoq">
                            BONUS
                          </span>}
                        {item.label}
                      </span>
                    </li>
                  </React.Fragment>;
            })}
            </ul>

            <div className="mx-auto mt-8 max-w-xl text-left" data-id="element-272" data-mp="ef3u3finmh5w">
              <p className="text-sm leading-6 text-inverse-foreground/70" data-id="element-273" data-mp="9hbq3n4qj4lc">
                You could continue collecting random workouts, saved videos,
                screenshots and disconnected class ideas.
              </p>
              <p className="mt-3 text-sm font-semibold text-accent-soft" data-id="element-274" data-mp="28p29rnfkcefx">
                Or you can have:
              </p>
              <ul className="mt-3 flex flex-wrap gap-1.5" data-id="element-275" data-mp="1u0pbukwx0lis">
                {alternatives.map(item => <li key={item} className="rounded-full border border-accent/30 bg-inverse-foreground/5 px-2.5 py-1 text-xs font-medium text-inverse-foreground" data-id="element-276" data-mp="1yyh0n629jrir">
                    {item}
                  </li>)}
              </ul>
              <p className="mt-3 text-sm leading-6 text-inverse-foreground/70" data-id="element-277" data-mp="2dp6e6f375ips">
                All organized inside one professional 6-in-1 Barre teaching
                system.
              </p>
            </div>

            <PriceTag label="Today" tone="dark" className="mt-10" data-id="element-278" data-mp="169jvm4n2br7y" />
            <div data-id="element-279" data-mp="16auze4gicyz2">
              <CheckoutButton variant="rose" className="mt-7 w-full sm:w-auto" data-id="element-280" data-mp="16774b0vf04i8">
                GET THE COMPLETE 6-IN-1 BARRE TEACHING SYSTEM
              </CheckoutButton>
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs font-medium text-inverse-foreground/70" data-id="element-281" data-mp="tt0om8h0kajz">
              {trustItems.map(({
              icon: Icon,
              label
            }) => <span key={label} className="flex items-center gap-1.5" data-id="element-282" data-mp="13pvkskptdyv3">
                  <Icon size={14} strokeWidth={1.75} className="text-accent" aria-hidden="true" data-id="element-283" data-mp="g0krtpeph8ri" />
                  {label}
                </span>)}
            </div>
          </div>
        </motion.div>

        <div className="mx-auto mt-14 max-w-2xl text-center" data-id="element-284" data-mp="pqvxbge1fq0i">
          <p className="font-display text-2xl font-semibold text-inverse-foreground sm:text-3xl" data-id="element-285" data-mp="ky2rsqaqwdnd">
            Barre Instructors Do Not Need More Random Exercises
          </p>
          <p className="mt-3 font-display text-xl italic text-accent-soft" data-id="element-286" data-mp="3ofck0y30g18w">
            They need structure. They need variety. They need progression. And
            they need a system they can actually use.
          </p>
          <p className="mt-5 text-base leading-7 text-inverse-foreground/70" data-id="element-287" data-mp="37ul323fygrtd">
            With Barre Instructor Ready, you do not have to begin every class
            with an empty page. {alreadyHave.join(' ')}
          </p>
          <p className="mt-4 text-base font-medium leading-7 text-inverse-foreground" data-id="element-288" data-mp="3gwkgymvobfs3">
            And with 150 ready-to-teach classes, you already have the class.
          </p>
        </div>
      </div>
    </section>;
}